import { useTranslation } from "react-i18next";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import { fpBandAccent } from "./FPBandDivider";
import { FPBand } from "../../lib/fpBands";
import { errorColor, calcPts } from "../../lib/fpScoring";

interface Club {
  id: string;
  name: string;
  shortName: string | null;
}

interface Prediction {
  clubId: string;
  position: number;
}

interface Props {
  clubs: Club[];
  /** Predictions sorted by predicted position, ascending — FPPredictionsView already sorts
   * this before passing it down, so this component stays a pure renderer. */
  sorted: Prediction[];
  actuals: Map<string, number>;
  N: number;
  bonus: number;
  isPreparing: boolean;
  /** Position-indexed bands (result[i] = band for position i + 1) — same accent as the
   * desktop SVG's left-edge labels and the drag-to-rank prediction screen. */
  bands: FPBand[];
}

/**
 * Mobile rendering of Final Positions' predicted-vs-actual view — one row per club instead
 * of the desktop SVG parallel-coordinates chart, which scales unreadably small once its
 * fixed-width right rail (260px) claims most of a 320-390px viewport. See DECISIONS.md.
 */
export default function FPPositionsList({ clubs, sorted, actuals, N, bonus, isPreparing, bands }: Props) {
  const { t } = useTranslation();

  return (
    <div>
      {sorted.map((p) => {
        const club = clubs.find((c) => c.id === p.clubId);
        const label = club?.shortName ?? club?.name ?? p.clubId;
        const band = bands[p.position - 1];
        const accent = band ? fpBandAccent(band) : { color: "var(--color-text-dim)", bg: "var(--color-surface-alt)" };
        const actualPos = actuals.get(p.clubId);
        const hasActual = !isPreparing && actualPos !== undefined;
        const error = hasActual ? Math.abs(p.position - actualPos!) : 0;
        const pts = hasActual ? calcPts(error, N, bonus) : null;
        const ptsColor = hasActual ? errorColor(error) : "var(--color-text-dim)";

        return (
          <div
            key={p.clubId}
            style={{
              display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 1rem",
              borderBottom: "1px solid var(--color-border-subtle)",
              borderLeft: `3px solid ${accent.color}`,
              opacity: hasActual || isPreparing ? 1 : 0.6,
            }}
          >
            <ClubBadge config={getClubBadge(club?.name ?? "")} size={22} title={club?.name ?? ""} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </div>
              <div style={{ fontSize: "0.7rem", color: "var(--color-text-dim)", marginTop: 1 }}>
                {hasActual
                  ? t("team.fp.predictedToActual", { predicted: p.position, actual: actualPos })
                  : t("team.fp.predictedToActualPending", { predicted: p.position })}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "0.95rem", fontWeight: 700, color: ptsColor, lineHeight: 1 }}>
                {pts ?? "—"}
              </div>
              <div style={{ fontSize: "0.6rem", color: "var(--color-text-dim)", marginTop: 1 }}>
                {t("team.fp.pts")}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
